import type { ClientOrganizationCreateRequestTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrganizationDataExtensionDebugFrame } from '@/features/profile-setting/components/organization-extensions/OrganizationDataExtensionDebugFrame';
import { getActiveExtensionSections } from '@/features/profile-setting/components/organization-extensions/sectionRegistry';
import type { CreateOrganizationForm } from '@/features/profile-setting/schemas/createOrganization';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import type { DropdownItem } from '@/shared/components/DropdownSelect';
import { FormGeoAreaSelect } from '@/shared/components/forms/FormGeoAreaSelect';
import { FormSingleSelect } from '@/shared/components/forms/FormSingleSelect';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';

type Props = {
  control: Control<CreateOrganizationForm>;
  tagOptions: DropdownItem<ClientOrganizationCreateRequestTagsEnum>[];
  isLoadingCreatableTypes: boolean;
  organizationId: string;
  selectedTags: ClientOrganizationCreateRequestTagsEnum[];
  onTagsChange: (tags: ClientOrganizationCreateRequestTagsEnum[]) => void;
};

export const OrganizationDataStep: FC<Props> = ({
  control,
  tagOptions,
  isLoadingCreatableTypes,
  organizationId,
  selectedTags,
  onTagsChange
}) => {
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization-create'
  });
  const { t: tOrg } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.organization'
  });

  const activeExtensions = useMemo(
    () => getActiveExtensionSections(selectedTags),
    [selectedTags]
  );

  return (
    <>
      <OrganizationDataExtensionDebugFrame label="Organization">
        <FormTextInput
          control={control}
          name="name"
          mode="outlined"
          label={t('fields.name')}
          maxLength={255}
        />
        <FormSingleSelect
          control={control}
          name="tags"
          options={tagOptions}
          label={t('fields.tags')}
          placeholder={
            isLoadingCreatableTypes
              ? t('placeholders.tagsLoading')
              : t('placeholders.tags')
          }
          editable={!isLoadingCreatableTypes}
          matchInputWidth
          onSelect={onTagsChange}
        />
        <FormGeoAreaSelect
          control={control}
          organizationId={organizationId}
          name="geoAreaIds"
          label={t('fields.geoArea')}
          placeholder={tOrg('geoAreaPicker.selectArea')}
          helperText={t('fields.geoAreaHelper')}
          sheetTitle={tOrg('geoAreaPicker.title')}
        />
      </OrganizationDataExtensionDebugFrame>
      {activeExtensions.map((ext) => {
        if (!ext.CreateSection) {
          return <></>;
        }
        return (
          <OrganizationDataExtensionDebugFrame
            key={ext.dataKey}
            label={`OrganizationDataExtension: ${ext.dataKey}`}
            color="dataExtension"
          >
            <ext.CreateSection namePrefix={`data.${ext.dataKey}`} />
          </OrganizationDataExtensionDebugFrame>
        );
      })}
    </>
  );
};
